import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'

import { api, ApiAssociatedAccount } from '@/api/rtk-api'
import {
  Alert,
  Card,
  ConfirmUsernameField,
  LabelValueRow,
  OperationButton
} from '@/components/core-v2'
import { CURRENCY, PATH, TENANT } from '@/const'
import { formatCurrencyFull } from '@/utilities/currency'
import { useUser } from '@/utilities/hooks/useUser'

import classes from './close-tab.module.scss'

interface Props {
  isClosed: boolean
  isLocked: boolean
  isLockedForCollateral: boolean
  trades: ApiAssociatedAccount[]
}
export const CloseTab = ({ isClosed, isLocked, isLockedForCollateral, trades }: Props) => {
  const { noteId } = useParams()
  const form = useForm()
  const navigate = useNavigate()

  const [closeNote, result] = api.endpoints.getByTenantNoteAndNoteIdClose.useLazyQuery()

  useEffect(() => {
    if (result.isSuccess) {
      api.util.invalidateTags(['Note'] as any)
      navigate(PATH.reportsNotes)
      toast.success('Note successfully closed')
    }
  }, [navigate, result])

  const { data: user } = useUser()

  if (!user) return null

  if (isClosed)
    return (
      <Alert type="warning">
        This action is not available because all trades within the note have been closed.
      </Alert>
    )

  if (isLockedForCollateral) {
    return (
      <Alert type="warning">
        This action cannot be performed, because this note is used as collateral for a loan
      </Alert>
    )
  }

  if (isLocked)
    return (
      <Alert type="warning">
        This note is currently locked for editing and cannot be closed at this time.
      </Alert>
    )

  const onSubmit = () => {
    closeNote({ tenant: TENANT, noteId })
  }

  return (
    <Card.Wrapper className={classes.root}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <h2>Close all trades</h2>
          <p>By proceeding with this instruction to close the note the following will occur:</p>
          <ul>
            <li>All holdings inside the note will be sold.</li>
            <li>
              All proceeds of the sale of the holdings (after deductions to cover any outstanding
              fees and loan payments) will be repaid to the holder of the note.
            </li>
          </ul>
          <p>Please carefully consider if you wish to proceed.</p>
          {trades.map((trade, index) => (
            <LabelValueRow
              key={trade.accountId}
              label={trade.accountName}
              showBottomBorder={index + 1 === trades.length}
              value={formatCurrencyFull(trade.accountValue, CURRENCY)}
            />
          ))}
          <ConfirmUsernameField id="confirmUsername" label="Please enter your username" />
          <OperationButton isLoading={result.isLoading} operationType="progress" type="submit">
            Close all trades
          </OperationButton>
        </form>
      </FormProvider>
    </Card.Wrapper>
  )
}
